import { useState } from 'react';
import { Select, Button, TextArea } from 'common/components';
import { LiveKitContextType } from 'contexts/LiveKitContext';
import { useUpdateContactStatus } from 'services/hooks/mutations';
import { toast } from 'react-toastify';

import styles from './UpdateContactStatus.module.scss';

type UpdateContactStatusProps = {
    statuses: LiveKitContextType['availableStatuses'];
    contactId?: string;
    onStatusUpdateSuccess?: (status: string) => void;
    statusLabel?: string;
    initialStatus?: string;
};

export const UpdateContactStatus = ({
    statuses,
    contactId,
    initialStatus,
    statusLabel = 'Contact Status',
    onStatusUpdateSuccess,
}: UpdateContactStatusProps) => {
    const [contactStatus, setContactStatus] = useState('');
    const [note, setNote] = useState('');

    const { updateContactStatus, isPending: isSubmitting } = useUpdateContactStatus();

    // Format status for display (NEW_LEAD -> New Lead)
    const formatStatus = (status: string) => {
        return status
            .split('_')
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
    };

    const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const sanitizedNote = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '');
        setNote(sanitizedNote);
    };

    const handleUpdate = async () => {
        if (!contactStatus) {
            return;
        }

        if (!contactId) {
            toast.error('Unable to update: No contact ID available');
            return;
        }

        updateContactStatus(
            { id: contactId, status: contactStatus, note },
            {
                onSuccess: () => {
                    setContactStatus('');
                    setNote('');
                    toast.success('Status updated successfully.');
                    if (onStatusUpdateSuccess) onStatusUpdateSuccess(contactStatus);
                },
                onError: (error) => {
                    toast.error('Failed to update status. Please try again.');
                },
            }
        );
    };

    return (
        <div className={styles.updateContactSection}>
            <h3>Update Contact Status</h3>
            <div className={styles.formGroup}>
                <Select
                    label={statusLabel}
                    value={contactStatus}
                    onChange={(e) => setContactStatus(e.target.value)}
                    disabled={isSubmitting}
                    options={[
                        { value: '', label: 'Select status' },
                        ...statuses
                            .filter((status) => status !== initialStatus)
                            .map((status) => ({ value: status, label: formatStatus(status) })),
                    ]}
                />
                <TextArea
                    label='Note'
                    helperText='Enter a note for the status update'
                    value={note}
                    disabled={isSubmitting}
                    onChange={handleNoteChange}
                />
            </div>

            <Button
                variant='primary'
                className='w-full'
                onClick={handleUpdate}
                disabled={isSubmitting || !contactStatus || !contactId}
            >
                {isSubmitting ? 'Updating...' : 'Update Status'}
            </Button>
        </div>
    );
};
